import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LlmLoggingService } from './llm-logging.service';
import { Tools_aiConsoleService } from './tools_ai-console.service';
import { Router } from '@angular/router';

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
    private llmLoggingService = inject(LlmLoggingService);
    private consoleService = inject(Tools_aiConsoleService);
    private router = inject(Router);

    handleError(error: any): void {
        // Extraer información detallada del error
        const errorDetails = this.extractErrorDetails(error);

        // Log en consola local para debugging inmediato
        this.consoleService.error(
            `[GlobalErrorHandler] ${errorDetails.message}`,
            error,
            '$CustomErrorHandler $handleError'
        );

        // Enviar log detallado al backend
        this.llmLoggingService.error(
            errorDetails.message,
            error,
            {
                ...errorDetails.context,
                severity: this.determineSeverity(error),
                errorType: errorDetails.type,
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                handled: false // Marca que este error no fue manejado explícitamente
            },
            errorDetails.component || 'Llm_module',
            'handleError'
        );
    }

    /**
     * Extrae información detallada del error según su tipo
     */
    private extractErrorDetails(error: any): {
        message: string;
        type: string;
        component?: string;
        context: Record<string, any>;
    } {
        // Error HTTP
        if (error instanceof HttpErrorResponse) {
            return {
                message: `HTTP Error ${error.status}: ${error.message}`,
                type: 'HttpError',
                context: {
                    status: error.status,
                    statusText: error.statusText,
                    url: error.url,
                    errorBody: error.error,
                    headers: this.extractHeaders(error.headers)
                }
            };
        }

        // Error de promesa rechazada
        if (error?.promise && error?.rejection) {
            return {
                message: `Unhandled Promise Rejection: ${error.rejection?.message || error.rejection}`,
                type: 'PromiseRejection',
                context: {
                    promise: error.promise?.toString(),
                    rejection: error.rejection,
                    zone: error.zone?.name
                }
            };
        }

        // Error de Angular (change detection, template, etc)
        if (error?.ngDebugContext) {
            return {
                message: error.message || 'Angular Error',
                type: 'AngularError',
                component: error.ngDebugContext?.component?.constructor?.name,
                context: {
                    view: error.ngDebugContext?.view?.def?.nodes
                        ?.map((n: any) => n?.element?.name)
                        .filter(Boolean)
                        .join(', '),
                    renderNodeIndex: error.ngDebugContext?.renderNodeIndex,
                    nodeDef: error.ngDebugContext?.nodeDef
                }
            };
        }

        // Error de JavaScript estándar
        if (error instanceof Error) {
            return {
                message: error.message,
                type: error.name,
                context: {
                    stack: error.stack,
                    // Intenta extraer el componente del stack trace
                    possibleComponent: this.extractComponentFromStack(error.stack)
                }
            };
        }

        // Error desconocido
        return {
            message: error?.toString() || 'Unknown error',
            type: 'UnknownError',
            context: {
                rawError: JSON.stringify(error, Object.getOwnPropertyNames(error))
            }
        };
    }

    /**
     * Determina la severidad del error
     */
    private determineSeverity(error: any): 'low' | 'medium' | 'high' | 'critical' {
        // Errores HTTP
        if (error instanceof HttpErrorResponse) {
            if (error.status >= 500) return 'critical';
            if (error.status >= 400) return 'high';
            return 'medium';
        }

        // Errores de seguridad
        // Errores de red
        // Errores de promesas no manejadas
        // Errores de Angular
        if (error?.message?.toLowerCase().includes('security')) return 'critical';
        if (error?.message?.toLowerCase().includes('network')) return 'high';
        if (error?.promise) return 'high';
        if (error?.ngDebugContext) return 'medium';

        return 'medium';
    }

    /**
     * Extrae el nombre del componente del stack trace
     */
    private extractComponentFromStack(stack?: string): string | undefined {
        if (!stack) return undefined;

        const componentMatch = stack.match(/at (\w+Component)\./);
        return componentMatch?.[1];
    }

    /**
     * Extrae headers HTTP de forma segura
     */
    private extractHeaders(headers: any): Record<string, string> {
        const result: Record<string, string> = {};

        try {
            headers.keys()?.forEach((key: string) => {
                // No registrar headers sensibles
                if (!this.isSensitiveHeader(key)) {
                    result[key] = headers.get(key);
                }
            });
        } catch (e) {
            // Ignora errores al extraer headers
        }

        return result;
    }

    /**
     * Verifica si un header es sensible
     */
    private isSensitiveHeader(headerName: string): boolean {
        const sensitive = ['authorization', 'cookie', 'x-api-key', 'token'];
        return sensitive.some(s => headerName.toLowerCase().includes(s));
    }
}
